import { useState, useEffect, useRef, useCallback } from "react";
import { CsrAPI } from "../apis/CsrAPI";

/**
 * Custom hook for polling table extraction status
 * Prevents duplicate requests when multiple components need the same status
 *
 * @param {string} studyId - The study ID to poll status for
 * @param {number} intervalMs - Polling interval in milliseconds (default: 5000)
 * @returns {Object} - Status object with isReady, isLoading, error, and manual trigger function
 */
export const useTableStatusPolling = (studyId, intervalMs = 5000) => {
  const [isReady, setIsReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastChecked, setLastChecked] = useState(null);

  const intervalRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Global state to track active polling for each study
  const globalPollingState = useRef(new Map());

  // Check if this study is already being polled
  const isStudyBeingPolled = useCallback(() => {
    return globalPollingState.current.has(studyId);
  }, [studyId]);

  // Mark this study as being polled
  const markStudyAsPolling = useCallback(() => {
    globalPollingState.current.set(studyId, true);
  }, [studyId]);

  // Mark this study as no longer being polled
  const unmarkStudyAsPolling = useCallback(() => {
    globalPollingState.current.delete(studyId);
  }, [studyId]);

  // Check table status
  const checkStatus = useCallback(async () => {
    if (!studyId) return;

    try {
      setIsLoading(true);
      setError(null);

      // Create new abort controller for this request
      abortControllerRef.current = new AbortController();

      const response = await CsrAPI.getAllStatuses({
        studyId,
      });

      const status = response.study_files === true;
      setIsReady(status);
      setLastChecked(new Date());

      // If status is ready, stop polling
      if (status) {
        stopPolling();
      }
    } catch (err) {
      if (err.name === "AbortError") {
        // Request was cancelled, don't set error
        return;
      }

      console.error("Error checking table status:", err);
      setError(err.message || "Failed to check table status");
    } finally {
      setIsLoading(false);
    }
  }, [studyId]);

  // Start polling
  const startPolling = useCallback(() => {
    if (!studyId || isStudyBeingPolled()) {
      return;
    }

    // Mark this study as being polled
    markStudyAsPolling();

    // Initial check
    checkStatus();

    // Set up interval
    intervalRef.current = setInterval(() => {
      checkStatus();
    }, intervalMs);
  }, [
    studyId,
    intervalMs,
    checkStatus,
    isStudyBeingPolled,
    markStudyAsPolling,
  ]);

  // Stop polling
  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Mark this study as no longer being polled
    unmarkStudyAsPolling();
  }, [unmarkStudyAsPolling]);

  // Manual trigger to check status immediately
  const triggerCheck = useCallback(() => {
    checkStatus();
  }, [checkStatus]);

  // Effect to start polling when studyId changes
  useEffect(() => {
    if (studyId) {
      startPolling();
    }

    // Cleanup on unmount or studyId change
    return () => {
      stopPolling();
    };
  }, [studyId, startPolling, stopPolling]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopPolling();
    };
  }, [stopPolling]);

  return {
    isReady,
    isLoading,
    error,
    lastChecked,
    triggerCheck,
    startPolling,
    stopPolling,
  };
};
